// TrajectoryChart — full-bet weight trajectory. Plots the planned weekly
// target glide path (violet, dashed — the plan) with actual weigh-ins
// overlaid (solid ink — you). "You are here" marker on the actual line at
// the current week.
//
// Pure SVG, no chart library. Models the same minimal-style approach as
// WeightSparkline but adds axes, block boundaries, and endpoint labels
// since this chart spans the full 245-day arc rather than a 14-day window.

import type { WeeklyMilestone } from '../lib/api';

export type TrajectoryActual = {
  week_number: number;
  weight_kg: number;
};

const VIEW_W = 320;
const VIEW_H = 150;
const PAD_TOP = 14;
const PAD_BOTTOM = 22; // room for "W1 W12 W24 W35" labels
const PAD_LEFT = 4;
const PAD_RIGHT = 4;

export function TrajectoryChart({
  milestones,
  actuals,
  currentWeek,
  currentWeightKg,
}: {
  milestones: WeeklyMilestone[];
  actuals: TrajectoryActual[];
  // The week the user is currently in (1-based). Used for the "you are here"
  // marker. Pass undefined for pre-bet — no marker drawn.
  currentWeek: number | undefined;
  // The user's latest weight. Drawn as a distinct green dot at the current
  // week so "where I am vs. the target line" is readable at a glance. Null /
  // undefined (not weighed yet) → no dot.
  currentWeightKg?: number | null;
}) {
  if (milestones.length < 2) {
    return (
      <div
        className="flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint"
        style={{ minHeight: 120 }}
      >
        Trajectory unavailable
      </div>
    );
  }

  const totalWeeks = milestones[milestones.length - 1].week_number;
  const startWeight = milestones[0].target_weight_kg;
  const endWeight = milestones[milestones.length - 1].target_weight_kg;

  // Drop actuals outside the bet's week range. Pre-bet weigh-ins (week 0 or
  // negative) and post-deadline ones would render off-chart and distort the
  // y-domain. Defensive — the binActualsByWeek caller already filters
  // pre-bet days, but a custom backend path could feed anything.
  const inRangeActuals = actuals.filter(
    (a) => a.week_number >= 1 && a.week_number <= totalWeeks,
  );

  // Y domain: include all milestone targets + in-range actuals, padded by
  // 0.5kg. If the user is hovering exactly on the line and there are no
  // actuals, padding alone keeps the line off the borders.
  // The current-weight marker is only drawn when the user is within the bet
  // window. Compute that here so the y-domain can include it (otherwise a
  // weight above/below the planned range would clip at the chart edge).
  const showCurrentDot =
    currentWeightKg != null &&
    currentWeek !== undefined &&
    currentWeek >= 1 &&
    currentWeek <= totalWeeks;

  const ys = [
    ...milestones.map((m) => m.target_weight_kg),
    ...inRangeActuals.map((a) => a.weight_kg),
    ...(showCurrentDot ? [currentWeightKg as number] : []),
  ];
  let yMin = Math.min(...ys) - 0.5;
  let yMax = Math.max(...ys) + 0.5;
  // Degenerate domain (all values identical even after padding shouldn't
  // happen, but be safe): force at least 1kg of vertical range so the line
  // doesn't collapse to a horizontal stroke at y=0.
  if (yMax - yMin < 1) {
    const mid = (yMax + yMin) / 2;
    yMin = mid - 0.5;
    yMax = mid + 0.5;
  }

  const plotW = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM;

  // Map week 1..totalWeeks → x within plot area.
  const xFor = (week: number) =>
    PAD_LEFT + ((week - 1) / Math.max(1, totalWeeks - 1)) * plotW;
  // Map weight (lower = further down) → y within plot area. yMax-yMin is
  // guaranteed ≥ 1 by the degenerate-domain check above.
  const yFor = (kg: number) =>
    PAD_TOP + ((yMax - kg) / (yMax - yMin)) * plotH;

  const targetPath = milestones
    .map((m, i) => {
      const cmd = i === 0 ? 'M' : 'L';
      return `${cmd}${xFor(m.week_number).toFixed(1)} ${yFor(m.target_weight_kg).toFixed(1)}`;
    })
    .join(' ');

  // Safe zone — everything at or below the target threshold (lower weight is
  // winning). Fill the band under the glide path so "stay under the line" reads
  // at a glance. Polygon = target path, then down the right edge, across the
  // bottom, and closed.
  const chartBottom = PAD_TOP + plotH;
  const firstX = xFor(milestones[0].week_number);
  const lastX = xFor(milestones[milestones.length - 1].week_number);
  const safeZonePath = `${targetPath} L${lastX.toFixed(1)} ${chartBottom.toFixed(1)} L${firstX.toFixed(1)} ${chartBottom.toFixed(1)} Z`;

  // Actual line — break the path on each gap so missing weeks don't draw a
  // straight segment across them. Sort by week first to be defensive.
  const sortedActuals = [...inRangeActuals].sort((a, b) => a.week_number - b.week_number);
  let actualPath = '';
  let prevWeek: number | null = null;
  for (const a of sortedActuals) {
    const isContiguous = prevWeek !== null && a.week_number === prevWeek + 1;
    const cmd = isContiguous ? 'L' : 'M';
    actualPath += ` ${cmd}${xFor(a.week_number).toFixed(1)} ${yFor(a.weight_kg).toFixed(1)}`;
    prevWeek = a.week_number;
  }

  // "You are here" — most recent actual weigh-in at or before currentWeek.
  // Pre-bet currentWeek (0 or negative) is treated as "no week yet" — no dot.
  const youDot = (() => {
    if (currentWeek === undefined || currentWeek < 1) return null;
    const candidates = sortedActuals.filter((a) => a.week_number <= currentWeek);
    if (candidates.length === 0) return null;
    const last = candidates[candidates.length - 1];
    return { x: xFor(last.week_number), y: yFor(last.weight_kg), kg: last.weight_kg };
  })();

  // Block boundaries — week 13 (B start) and week 25 (C start) for the
  // canonical 35-week bet. Compute from the milestone array so custom bets
  // get correct guides.
  const boundaries = computeBlockBoundaries(milestones);

  // X-axis tick weeks: always start + end, plus the boundary weeks if any.
  const tickWeeks = uniqueSorted([
    1,
    ...boundaries.map((b) => b.week),
    totalWeeks,
  ]);

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Weight trajectory week ${currentWeek ?? 0} of ${totalWeeks}: target ${endWeight}kg by deadline`}
      className="w-full"
      style={{ maxHeight: 180 }}
    >
      <defs>
        {/* The one gradient in the system: a green wash under the glide path,
            so "stay under the line" reads as the winning half of the plot. */}
        <linearGradient id="traj-safe" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-good-mid)" stopOpacity="0.12" />
          <stop offset="68%" stopColor="var(--color-good-mid)" stopOpacity="0.03" />
          <stop offset="100%" stopColor="var(--color-good-mid)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Safe zone — at or below the target threshold is winning */}
      <path d={safeZonePath} fill="url(#traj-safe)" stroke="none" />

      {/* Block boundary vertical guides */}
      {boundaries.map((b) => (
        <line
          key={`b-${b.week}`}
          x1={xFor(b.week)}
          x2={xFor(b.week)}
          y1={PAD_TOP}
          y2={PAD_TOP + plotH}
          stroke="currentColor"
          strokeWidth={1}
          className="text-line"
        />
      ))}

      {/* Target threshold — the plan. Violet and dashed, so it reads as
          "scheduled, not yet real" by hue and by texture. */}
      <path
        d={targetPath}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeDasharray="5 4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-pop-mid"
      />

      {/* Actual weigh-ins — solid ink, the one emphatic line */}
      {actualPath && (
        <path
          d={actualPath.trim()}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          className="text-fg draw-in"
        />
      )}

      {/* Small dots on each actual data point */}
      {sortedActuals.map((a) => (
        <circle
          key={`a-${a.week_number}`}
          cx={xFor(a.week_number)}
          cy={yFor(a.weight_kg)}
          r={1.5}
          fill="currentColor"
          className="text-fg"
        />
      ))}

      {/* "You are here" larger dot — on the actual weigh-in trend line */}
      {youDot && (
        <circle
          cx={youDot.x}
          cy={youDot.y}
          r={3.5}
          fill="currentColor"
          stroke="var(--color-bg-raised, #ffffff)"
          strokeWidth={1.5}
          className="text-fg"
        />
      )}

      {/* Current weight — distinct green dot at the current week, so the gap
          between where you are and the target glide path is obvious. Drawn
          last so it sits on top of the trend marker if they coincide. */}
      {showCurrentDot && (
        <circle
          cx={xFor(currentWeek as number)}
          cy={yFor(currentWeightKg as number)}
          r={3.5}
          fill="currentColor"
          stroke="var(--color-bg-raised, #ffffff)"
          strokeWidth={1.5}
          className="text-good-mid"
        >
          <title>{`Current: ${(currentWeightKg as number).toFixed(1)}kg (week ${currentWeek})`}</title>
        </circle>
      )}

      {/* Endpoint labels: starting weight at top-left; target floated just
          above the line's flat tail so the dash never strikes through it */}
      <text
        x={PAD_LEFT}
        y={Math.max(10, yFor(startWeight) - 4)}
        className="fill-fg-muted"
        fontSize="9"
        fontFamily="var(--font-mono)"
      >
        {startWeight.toFixed(1)}kg
      </text>
      <text
        x={VIEW_W - PAD_RIGHT}
        y={Math.min(VIEW_H - PAD_BOTTOM - 2, yFor(endWeight) - 8)}
        textAnchor="end"
        className="fill-fg-muted"
        fontSize="9"
        fontFamily="var(--font-mono)"
      >
        {endWeight.toFixed(1)}kg
      </text>

      {/* X-axis week labels */}
      {tickWeeks.map((wk) => (
        <text
          key={`tx-${wk}`}
          x={xFor(wk)}
          y={VIEW_H - 8}
          textAnchor={wk === 1 ? 'start' : wk === totalWeeks ? 'end' : 'middle'}
          className="fill-fg-faint"
          fontSize="8"
          fontFamily="var(--font-mono)"
        >
          W{wk}
        </text>
      ))}

      {/* Block letter labels (A/B/C) centered over each block range */}
      {computeBlockRanges(milestones, totalWeeks).map((br) => (
        <text
          key={`bl-${br.block}-${br.start}`}
          x={(xFor(br.start) + xFor(br.end)) / 2}
          y={PAD_TOP - 3}
          textAnchor="middle"
          className="fill-fg-faint"
          fontSize="8"
          fontFamily="var(--font-mono)"
          letterSpacing="1"
        >
          {br.block}
        </text>
      ))}
    </svg>
  );
}

function computeBlockBoundaries(
  milestones: WeeklyMilestone[],
): { week: number; from: string; to: string }[] {
  const out: { week: number; from: string; to: string }[] = [];
  for (let i = 1; i < milestones.length; i++) {
    if (milestones[i].block !== milestones[i - 1].block) {
      out.push({
        week: milestones[i].week_number,
        from: milestones[i - 1].block,
        to: milestones[i].block,
      });
    }
  }
  return out;
}

function computeBlockRanges(
  milestones: WeeklyMilestone[],
  totalWeeks: number,
): { block: string; start: number; end: number }[] {
  const out: { block: string; start: number; end: number }[] = [];
  let curBlock = milestones[0].block;
  let curStart = milestones[0].week_number;
  for (let i = 1; i < milestones.length; i++) {
    if (milestones[i].block !== curBlock) {
      out.push({ block: curBlock, start: curStart, end: milestones[i].week_number - 1 });
      curBlock = milestones[i].block;
      curStart = milestones[i].week_number;
    }
  }
  out.push({ block: curBlock, start: curStart, end: totalWeeks });
  return out;
}

function uniqueSorted(nums: number[]): number[] {
  return [...new Set(nums)].sort((a, b) => a - b);
}
